import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from "antd";
import { ApiService } from "../../api";
import { useCloseOwnTab } from "../../tabs/tabContext";

import { PageHeader } from "../../components/PageHeader";

const { Text } = Typography;

/**
 * Create a template.
 *
 * Only the handful of things that cannot be changed later, or that the template
 * needs before anything else will work: what it is called, the short name that
 * ends up in every permit's number, and who administers it. Sections, stages
 * and approvals all come afterwards on the setup page — asking for them here
 * would be a wizard nobody could finish in one sitting.
 */
const TemplateCreatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const closeOwnTab = useCloseOwnTab();
  const [form] = Form.useForm();

  const [types, setTypes] = useState<Array<{ id: number; name: string }>>([]);
  const [groups, setGroups] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [typeRows, groupRows] = await Promise.all([
          ApiService.apiMainTemplateTypesList("admin"),
          ApiService.apiAccountsGroupsList(),
        ]);
        setTypes(
          (typeRows as any[]).map((row) => ({ id: row.id, name: row.name }))
        );
        setGroups(
          (groupRows as any[]).map((row) => ({ id: row.id, name: row.name }))
        );
      } catch {
        // A missing list is not fatal: the type picker simply comes back empty
        // and the form refuses to submit until one can be chosen.
      }
      setLoading(false);
    })();
  }, []);

  const create = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const created = await ApiService.apiMainTemplateCreate({
        name: values.name.trim(),
        short_name: values.short_name.trim(),
        template_type: values.template_type,
        view_type: values.view_type,
        admins: values.admins ?? null,
        users: values.users ?? null,
      });
      message.success(t("Template created"));
      // Straight into setting it up; leaving the create form behind would only
      // invite a second template by accident.
      closeOwnTab();
      navigate(`/templates/${created.short_name}/setup`);
    } catch (e: any) {
      const body = e?.body;
      if (body?.short_name) {
        form.setFields([
          { name: "short_name", errors: [String(body.short_name[0] ?? body.short_name)] },
        ]);
      } else {
        message.error(body?.message ?? t("Something went wrong"));
      }
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t("New template")} />

      <Card size="small" style={{ maxWidth: 620 }}>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ view_type: "Collapse" }}
        >
          <Form.Item
            name="name"
            label={t("Name")}
            rules={[{ required: true, message: t("A name is required.") }]}
          >
            <Input placeholder={t("e.g. Hot Work Permit")} autoFocus />
          </Form.Item>

          <Form.Item
            name="short_name"
            label={t("Short name")}
            extra={
              <Text type="secondary" style={{ fontSize: 12 }}>
                {t(
                  "Appears in every permit's number and web address, so it cannot be changed later."
                )}
              </Text>
            }
            rules={[
              { required: true, message: t("A short name is required.") },
              {
                pattern: /^[A-Za-z0-9_-]+$/,
                message: t("Letters, numbers, dashes and underscores only."),
              },
            ]}
          >
            <Input placeholder={t("e.g. HWP")} />
          </Form.Item>

          <Form.Item
            name="template_type"
            label={t("Type")}
            rules={[{ required: true, message: t("A type is required.") }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t("Pick a type")}
              options={types.map((row) => ({ value: row.id, label: t(row.name) }))}
              notFoundContent={
                <Alert
                  type="warning"
                  showIcon
                  message={t("No template types you can administer.")}
                />
              }
            />
          </Form.Item>

          <Form.Item name="view_type" label={t("How sections are shown")}>
            <Select
              options={[
                { value: "Collapse", label: t("All at once, collapsible") },
                { value: "Step", label: t("One step at a time") },
              ]}
            />
          </Form.Item>

          <Form.Item name="admins" label={t("Administered by")}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("Pick a group")}
              options={groups.map((g) => ({ value: g.id, label: g.name }))}
            />
          </Form.Item>

          <Form.Item name="users" label={t("Raised by")}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={t("Pick a group")}
              options={groups.map((g) => ({ value: g.id, label: g.name }))}
            />
          </Form.Item>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t(
              "A new template starts with a working lifecycle — Draft, Approval, Active, Closed. You can rename or change all of it afterwards."
            )}
          />

          <Space>
            <Button type="primary" loading={saving} onClick={create}>
              {t("Create")}
            </Button>
            <Button onClick={() => navigate("/templates")}>{t("Cancel")}</Button>
          </Space>
        </Form>
      </Card>
    </div>
  );
};

export default TemplateCreatePage;
